/**
 * Rough Slimming World "syns" & Weight Watchers "points" estimators.
 *
 * These are UNOFFICIAL approximations calculated from a meal's macro breakdown
 * (kcal, protein_g, fat_g, carbs_g). They're intended as a helpful signal —
 * NOT a substitute for the official group tools. All meals should include a
 * disclaimer chip when displaying these values.
 *
 * Slimming World® and Weight Watchers® / WW® are trademarks of their respective
 * companies. We're not affiliated with, endorsed by, or licensed by either.
 */

/**
 * Rough Weight Watchers points estimate.
 * Loosely modeled on the "SmartPoints" era formula (2016–2022) which penalises
 * saturated fat & sugar and rewards protein. Without granular satfat/sugar
 * fields, we approximate using total fat and treat protein as a rebate.
 *
 *   points ≈ round((kcal + 4·fat_g − 2·protein_g) / 33)
 *
 * When macros aren't available (fresh AI search results), we fall back to a
 * kcal-only estimate: `~kcal / 33` — good enough for a first-glance signal.
 *
 * Yields ballpark figures: chicken salad ~4-6, lasagna ~15-20.
 */
export function wwPointsEstimate({ calories_per_serving, protein_g, fat_g } = {}) {
  if (!calories_per_serving || calories_per_serving <= 0) return null;
  const p = Number.isFinite(protein_g) ? protein_g : 0;
  const f = Number.isFinite(fat_g) ? fat_g : 0;
  const raw = (calories_per_serving + 4 * f - 2 * p) / 33;
  const pts = Math.max(0, Math.round(raw));
  return pts;
}

/**
 * Rough Slimming World syns estimate.
 * Free Foods (lean meat, fish, veg, eggs, pasta, rice, potatoes) count as 0 syns
 * on SW's plan, so syns come mostly from added fat, oils, and processed carbs.
 * A common rule of thumb: 5 g of fat ≈ 1 syn (butter/oil/cheese).
 *
 *   syns ≈ round(fat_g / 5)
 *
 * When fat_g isn't available, we approximate fat from kcal assuming ~35% of
 * calories come from fat (a rough UK-diet average): fat_g ≈ 0.35·kcal / 9.
 *
 * We floor at 0 and cap at 40 to keep the display sensible.
 */
export function slimmingWorldSynsEstimate({ calories_per_serving, fat_g } = {}) {
  let f = Number.isFinite(fat_g) ? fat_g : null;
  if (f == null) {
    if (!calories_per_serving || calories_per_serving <= 0) return null;
    // Assume ~35% of kcal from fat as a fallback when macros aren't loaded yet.
    f = (calories_per_serving * 0.35) / 9;
  }
  const syns = Math.max(0, Math.min(40, Math.round(f / 5)));
  return syns;
}
